import { reactive, toRaw, watch } from 'vue';
import { compareSemver, keptGrants, typeOwner, type CardInput, type Id, type ModuleManifest, type ObjectRecord, type Permission, type Style } from '@vp/shared-types';
import { CatalogInstallError, checkEnvelope, fetchForInstall, installState, openEnvelope } from './catalog';
import { API_VERSION, createModuleWorker, ModuleRuntime, parsePackage, type ModuleInfo, type WindowSpec } from '@vp/module-runtime';
import type { Editor, EditorHost, ModuleTextInfo, ToolDef } from '@vp/render';
import { DATA_FORMAT_VERSION, DEFAULT_TEXT_STYLE } from '@vp/core';
import { diagramNaturalSize } from '@vp/render';
import { availability, logger, metrics, reportStorageError, settings, storage } from './platform';
import { ask } from './dialogs';
import { locale, registerModuleMessages, t, tm } from './i18n';
import { live, ui } from './editor/bridge';
import { loadFamiliesOf, platformFaces, platformFontsForModules, registerModuleFonts } from './fonts';
import type { useApp } from './stores/app';
import { createDeviceAccess, pickFiles, readPicked, saveBlob } from './editor/device';
import { buildSrcdoc, messageSize, onSandboxReport, type SandboxDoc } from './editor/sandbox';
import { requestShowPanel, setModuleProfilePanels } from './profile-panels';
import { cardState, forgetCard, mergeCardState } from './profile-looks';

/** Integration of the module runtime (§7) with the editor UI. */
type App = ReturnType<typeof useApp>;

export const modulesState = reactive({ list: [] as ModuleInfo[], rev: 0, ready: false, /** bumped when module search texts arrive */ textRev: 0 });

type ViewListener = (moduleId: string, objectId: Id, data: unknown) => void;
const viewListeners = new Set<ViewListener>();
export function onViewPost(fn: ViewListener): () => void {
  viewListeners.add(fn);
  return () => viewListeners.delete(fn);
}

type ProfileCardListener = (moduleId: string, card: string, data: unknown) => void;
const profileCardListeners = new Set<ProfileCardListener>();
/** API 3.0: messages modules post to their profile cards. */
export function onProfileCardPost(fn: ProfileCardListener): () => void {
  profileCardListeners.add(fn);
  return () => profileCardListeners.delete(fn);
}
/** API 3.0: a message from a profile card to its module. */
export function sendProfileCardMessage(moduleId: string, card: string, data: unknown): void {
  runtime?.sendProfileCardMessage(moduleId, card, data);
}
/** API 4.10: the person used an interactive element of a module's card. */
export function profileCardInput(moduleId: string, card: string, input: CardInput): void {
  runtime?.profileCardInput(moduleId, card, input);
}
/** API 4.10: the module may show the profile's counters in its cards. */
export function moduleHasProfileStats(moduleId: string): boolean {
  return !!modulesState.list.find((m) => m.id === moduleId)?.granted.includes('profile.stats');
}
/** API 4.10: a policy refusal on a module's card page: a violation of the module. */
export function cardViolation(moduleId: string, directive: string): void {
  runtime?.sandboxViolation(moduleId, 'csp ' + directive);
}
const unsafeNoted = new Set<string>();
/** API 4.6: a sandbox page (of a view, window, panel or card) found itself not isolated and showed nothing. */
export function sandboxUnsafe(moduleId: string): void {
  logger.error('module.sandbox.unsafe', { moduleId });
  if (unsafeNoted.has(moduleId)) return;
  unsafeNoted.add(moduleId);
  appRef?.notify('error', t('modules.sandboxUnsafe'));
}
/** API 3.0: the HTML of a module's profile card. */
export function profileCardHtml(moduleId: string, card: string): string | null {
  return runtime?.profileCardHtml(moduleId, card) ?? null;
}

type PanelListener = (moduleId: string, data: unknown) => void;
const panelListeners = new Set<PanelListener>();
/** API 2.0: messages modules post to their panels. */
export function onPanelPost(fn: PanelListener): () => void {
  panelListeners.add(fn);
  return () => panelListeners.delete(fn);
}

/** API 2.3: open windows of modules (shown by ModuleWindows.vue). */
export interface ModuleWindow extends WindowSpec {
  /** Place the user dragged it to (null: centred). */
  x: number | null;
  y: number | null;
  offset: number;
  /** Messages waiting for the window's document to be there. */
  queue: unknown[];
}
export const moduleWindows = reactive<ModuleWindow[]>([]);
type WindowListener = (handle: string, data: unknown) => void;
const windowListeners = new Set<WindowListener>();
export function onWindowPost(fn: WindowListener): () => void {
  windowListeners.add(fn);
  return () => windowListeners.delete(fn);
}
/** A message from a window to its module. */
export function sendWindowMessage(handle: string, data: unknown): void {
  runtime?.windowMessage(handle, data);
}
/** The user (or the window itself) closed it. */
export function closeModuleWindow(handle: string): void {
  const i = moduleWindows.findIndex((w) => w.handle === handle);
  if (i >= 0) moduleWindows.splice(i, 1);
  runtime?.windowClosed(handle);
}

let runtime: ModuleRuntime | null = null;
let appRef: App | null = null;
const toolListeners = new Set<() => void>();

/** Text a module gives for its object (Find on canvas): string, null (none: search its drawing), undefined (asked for). */
export function moduleSearchText(o: ObjectRecord, rev: number): string | null | undefined {
  return runtime ? runtime.searchText(o, rev) : null;
}

export function getRuntime(): ModuleRuntime | null {
  return runtime;
}

function refreshList(): void {
  if (!runtime) return;
  modulesState.list = runtime.list();
  textInfoCache.clear();
  modulesState.rev++;
  for (const fn of toolListeners) fn();
  refreshDiagrams();
}

function invalidate(ids: Id[] | null): void {
  if (!appRef) return;
  // a module node form inside a DSkhema scheme is drawn for "<scheme id>::<node>": refresh the scheme
  const mapped = ids && [...new Set(ids.map((id) => (id.includes('::') ? id.slice(0, id.indexOf('::')) : id)))];
  for (const s of appRef.sessions.values()) s.scene.invalidateObjects(mapped);
  modulesState.textRev++;
}

/** DSkhema node forms from running modules: alias → object type (built-in form names win). */
export function diagramShapes(): Map<string, string> {
  return runtime?.diagramShapes() ?? new Map();
}

const textInfoCache = new Map<string, ModuleTextInfo | null>();
/** API 1.5: text of a module object type (manifest `objectTypes[].text`), or undefined. */
export function moduleTextInfo(type: string): ModuleTextInfo | undefined {
  if (typeOwner(type) === null || type.startsWith('core:')) return undefined;
  const hit = textInfoCache.get(type);
  if (hit !== undefined) return hit ?? undefined;
  const t0 = runtime?.typeInfo(type)?.contribution.text;
  const info: ModuleTextInfo | null = t0
    ? {
        inset: { top: t0.inset?.top ?? 0, right: t0.inset?.right ?? 0, bottom: t0.inset?.bottom ?? 0, left: t0.inset?.left ?? 0 },
        fit: t0.fit ?? false,
        style: { ...DEFAULT_TEXT_STYLE, align: 'left', valign: 'top', ...(t0.style ?? {}) },
      }
    : null;
  if (runtime) textInfoCache.set(type, info);
  return info ?? undefined;
}

export interface ToolbarButton {
  moduleId: string;
  command: string;
  label: string;
  /** inline SVG markup (shown as an isolated image) or a platform icon name */
  svg?: string;
  icon?: string;
  active: boolean;
}

/** API 1.5: module buttons for the top bar, for the selected objects. */
export function toolbarFor(objs: ObjectRecord[], platformIcons: ReadonlySet<string>): ToolbarButton[] {
  if (!runtime || !objs.length) return [];
  const out: ToolbarButton[] = [];
  for (const { moduleId, manifest } of runtime.contributions()) {
    if (!runtime.isModuleAvailable(moduleId)) continue;
    for (const b of manifest.contributes.toolbar ?? []) {
      const c = manifest.contributes.commands?.find((x) => x.id === b.command);
      if (!c) continue;
      const types = c.objectTypes?.map((x) => `${moduleId}:${x}`);
      const targets = types ? objs.filter((o) => types.includes(o.type)) : objs;
      if (!targets.length) continue;
      const active = !!b.active && targets.every((o) => o.props[b.active!.prop] === b.active!.equals);
      const svg = b.icon?.trimStart().startsWith('<svg') ? b.icon : undefined;
      out.push({ moduleId, command: c.id, label: tm(moduleId, c.name), svg, icon: !svg && b.icon && platformIcons.has(b.icon) ? b.icon : svg ? undefined : 'module', active });
    }
  }
  return out;
}

export interface ModuleFeedTool {
  moduleId: string;
  command: string;
  label: string;
  hint?: string;
  /** inline SVG markup (shown as an isolated image) or a platform icon name */
  svg?: string;
  icon: string;
  /** API 2.6: works on the home screen too, with no canvas open */
  home: boolean;
}

/** API 2.5: tools of running modules for the feed's Tools section — only where `feed` is granted. */
export function moduleFeedTools(platformIcons: ReadonlySet<string>): ModuleFeedTool[] {
  void modulesState.rev;
  const out: ModuleFeedTool[] = [];
  for (const m of modulesState.list) {
    if (m.state !== 'running' || !m.granted.includes('feed')) continue;
    for (const f of m.manifest.contributes.feedTools ?? []) {
      const c = m.manifest.contributes.commands?.find((x) => x.id === f.command);
      if (!c) continue;
      const svg = f.icon?.trimStart().startsWith('<svg') ? f.icon : undefined;
      out.push({ moduleId: m.id, command: c.id, label: tm(m.id, c.name), hint: f.hint ? tm(m.id, f.hint) : undefined, svg, icon: !svg && f.icon && platformIcons.has(f.icon) ? f.icon : 'module', home: !!f.home });
    }
  }
  return out;
}

/** Windows a feed tool opened ("module:command" → handles): a second press closes them. */
const feedToolWindows = reactive(new Map<string, string[]>());
const feedToolKey = (tool: ModuleFeedTool) => `${tool.moduleId}:${tool.command}`;

/** Windows the tool opened are still on screen. */
export function isFeedToolOpen(tool: ModuleFeedTool): boolean {
  const handles = feedToolWindows.get(feedToolKey(tool));
  return !!handles && moduleWindows.some((w) => handles.includes(w.handle));
}

/** API 2.5: a press on a module tool in the feed — runs its command, or closes what it opened. */
export async function runFeedTool(tool: ModuleFeedTool): Promise<void> {
  const key = feedToolKey(tool);
  if (isFeedToolOpen(tool)) {
    for (const handle of feedToolWindows.get(key) ?? []) if (moduleWindows.some((w) => w.handle === handle)) closeModuleWindow(handle);
    feedToolWindows.delete(key);
    return;
  }
  const before = new Set(moduleWindows.map((w) => w.handle));
  // API 2.6: a home tool pressed away from a canvas gets no selection
  await runCommand(tool.moduleId, tool.command, appRef?.view === 'editor' ? live.editor?.selection ?? [] : []);
  const opened = moduleWindows.filter((w) => w.moduleId === tool.moduleId && !before.has(w.handle)).map((w) => w.handle);
  if (opened.length) feedToolWindows.set(key, opened);
}

/** Display context of DSkhema schemes for a canvas scene. */
export function diagramCtx() {
  return {
    diagramShapes: diagramShapes(),
    locale: locale.value,
    outdatedDiagramLabel: t('diagram.outdatedVersion'),
    formatVersion: (type: string) => runtime?.typeFormatVersion(type),
    moduleDefaults: (type: string) => runtime?.typeInfo(type)?.contribution.defaultProps,
    moduleText: moduleTextInfo,
    moduleSimplify: (type: string) => !!runtime?.typeInfo(type)?.contribution.lod?.simplify,
  };
}

let lastShapes = '';
function refreshDiagrams(force = false): void {
  if (!appRef) return;
  const ctx = diagramCtx();
  const sig = [...ctx.diagramShapes].join(',') + '|' + ctx.locale;
  if (!force && sig === lastShapes) return;
  lastShapes = sig;
  for (const s of appRef.sessions.values()) {
    s.scene.displayCtx = { ...s.scene.displayCtx, ...ctx };
    const schemes = [...s.doc.allObjects()].filter((o) => o.type === 'core:diagram').map((o) => o.id);
    if (schemes.length) s.scene.invalidateObjects(schemes);
  }
}

function moduleName(id: string): string {
  const m = runtime?.get(id)?.manifest;
  return m ? tm(id, m.name) : id;
}

/** Shipped with the app (`apps/web/bundled-module.ts` writes the packages into `public/bundled/`). */
const SHIPPED_IDS = ['org.vp.excalidraw'];

function shippedUrl(file: string): string {
  const q = __BUILD_ID__ === 'dev' ? '' : `?${encodeURIComponent(__BUILD_ID__)}`;
  return new URL(`bundled/${file}${q}`, document.baseURI).href;
}

function olderThan(installed: string, shipped: string): boolean {
  try {
    return compareSemver(installed, shipped) < 0;
  } catch {
    return true;
  }
}

/**
 * Install the modules that ship with the app, and drop bundled modules this build no longer ships.
 * A newer copy already installed is left as it is. Switching a module off in the list stays off:
 * the same version is not installed again.
 */
async function syncShipped(): Promise<void> {
  if (!runtime) return;
  const shipped = new Set(SHIPPED_IDS);
  for (const m of runtime.list()) {
    if (!m.bundled || shipped.has(m.id)) continue;
    try {
      await runtime.uninstall(m.id);
    } catch (e) {
      logger.error('module.bundled.remove', { moduleId: m.id, error: e instanceof Error ? e.name : 'unknown' });
    }
  }
  for (const id of SHIPPED_IDS) await installShipped(id);
}

async function installShipped(id: string): Promise<void> {
  if (!runtime) return;
  let version = '';
  try {
    const meta = await fetch(shippedUrl(`${id}.json`));
    if (!meta.ok) throw new Error(String(meta.status));
    const body = (await meta.json()) as { version?: unknown };
    if (typeof body.version !== 'string' || !body.version) throw new Error('version');
    version = body.version;
  } catch (e) {
    logger.warn('module.bundled.fetch', { moduleId: id, error: e instanceof Error ? e.message : 'unknown' });
    return;
  }
  const have = runtime.get(id);
  if (have && !olderThan(have.version, version)) return;
  let data: Uint8Array;
  try {
    const res = await fetch(shippedUrl(`${id}.vpm`));
    if (!res.ok) throw new Error(String(res.status));
    data = new Uint8Array(await res.arrayBuffer());
    if (parsePackage(data).manifest.version !== version) throw new Error('version');
  } catch (e) {
    logger.error('module.bundled.invalid', { moduleId: id, error: e instanceof Error ? e.message : 'unknown' });
    return;
  }
  try {
    await runtime.install(data, { devMode: false, bundled: true });
  } catch (e) {
    logger.error('module.bundled.install', { moduleId: id, error: e instanceof Error ? e.message : 'unknown' });
  }
}

const userActive = (): boolean => !!(navigator as Navigator & { userActivation?: { isActive: boolean } }).userActivation?.isActive;

/** API 2.6: an SVG picture of a module's new canvas, drawn as PNG (scripts and outside files do not run in an <img>). */
async function svgToPng(data: Uint8Array): Promise<Uint8Array | null> {
  const url = URL.createObjectURL(new Blob([data as BlobPart], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    let w = img.naturalWidth || 300;
    let h = img.naturalHeight || 150;
    const k = Math.min(4, 4096 / Math.max(w, h), Math.max(1, 1024 / Math.max(w, h)));
    w = Math.max(1, Math.round(w * k));
    h = Math.max(1, Math.round(h * k));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/png'));
    return blob ? new Uint8Array(await blob.arrayBuffer()) : null;
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function initModules(app: App): Promise<void> {
  appRef = app;
  runtime = new ModuleRuntime({
    storage,
    createWorker: createModuleWorker,
    locale: () => locale.value,
    logger,
    metrics,
    docs: {
      doc: () => live.session?.doc ?? null,
      selection: () => live.editor?.selection ?? [],
      notify: (level, msg, _objectId, actions) => app.notify(level, msg, 5000, actions),
      canvasName: () => live.session?.meta.value.name ?? '',
      readOnly: () => ui.readOnly,
      setSelection: (ids) => live.editor?.select(ids),
      insertTree: (objects, connectors, at, module, ownOnly) => live.editor?.insertTree(objects, connectors, at, module, ownOnly) ?? [],
      focus: (ids) => live.editor?.fitObjects(ids),
      connect: (from, to, opts) => {
        const id = live.editor?.connectObjects(from, to, opts);
        if (!id) throw new Error('arrow not created');
        return id;
      },
    },
    onChange: refreshList,
    onInvalidate: invalidate,
    onMessages: (id, messages) => registerModuleMessages(id, messages),
    onViewPost: (m, o, d) => {
      for (const fn of viewListeners) fn(m, o, d);
    },
    onPanelPost: (m, d) => {
      for (const fn of panelListeners) fn(m, d);
    },
    profileCards: {
      getState: (id) => cardState(id),
      setState: (id, values) => void mergeCardState(id, values),
      forget: (id) => forgetCard(id),
      show: (id) => requestShowPanel(id),
    },
    onProfileCardPost: (m, c, d) => {
      for (const fn of profileCardListeners) fn(m, c, d);
    },
    windows: {
      allowed: userActive,
      open: (spec) => {
        // a little offset for each new window, so they do not cover each other exactly
        const n = moduleWindows.length;
        moduleWindows.push({ ...spec, html: buildSrcdoc(spec.html), x: null, y: null, offset: n * 28, queue: spec.data === undefined ? [] : [spec.data] });
      },
      close: (handle) => {
        const i = moduleWindows.findIndex((w) => w.handle === handle);
        if (i >= 0) moduleWindows.splice(i, 1);
      },
      post: (handle, data) => {
        for (const fn of windowListeners) fn(handle, data);
      },
      setTitle: (handle, title) => {
        const w = moduleWindows.find((x) => x.handle === handle);
        if (w) w.title = title;
      },
    },
    canvases: {
      create: async (meta, objects) => {
        const now = Date.now();
        try {
          await storage.createCanvasWithContent(
            { ...meta, createdAt: now, updatedAt: now, formatVersion: DATA_FORMAT_VERSION, lastExportAt: null, objectCount: objects.length },
            objects,
            [],
          );
        } catch (e) {
          reportStorageError(e);
          throw e;
        }
        logger.info('canvas.create', { canvasId: meta.id });
        await app.refreshCanvases();
      },
      open: (id) => {
        if (!userActive()) return false;
        void app.openCanvas(id);
        return true;
      },
      svgToPng,
    },
    device: createDeviceAccess((id) => moduleName(id)),
    imageSize: async (blob) => {
      const bmp = await createImageBitmap(blob);
      const size = { width: bmp.width, height: bmp.height };
      bmp.close();
      return size;
    },
    onIsolated: (id, reason) => app.notify('error', t('modules.isolatedMsg', { name: moduleName(id), reason })),
    onCrashed: (id) => app.notify('warning', t('modules.crashedMsg', { name: moduleName(id) })),
    diagramSize: (source) => diagramNaturalSize(source, { shapes: diagramShapes(), locale: locale.value }),
    fonts: { platform: platformFontsForModules, faces: platformFaces, registerModule: registerModuleFonts },
    onPrimitives: (prims) => {
      const stacks: string[] = [];
      for (const p of prims) if (p.k === 'text' && p.style.fontFamily) stacks.push(p.style.fontFamily);
      if (stacks.length) loadFamiliesOf(stacks);
    },
    onSearchText: () => modulesState.textRev++,
  });
  availability.impl = runtime;
  // API 3.0: the cards of running modules are in the profile's panel library
  watch(
    () => [modulesState.rev, locale.value] as const,
    () => {
      const panels = [];
      for (const m of modulesState.list) {
        if (m.state !== 'running') continue;
        // API 4.10: the manifest's cards and those the module made, with the module's look
        for (const c of runtime?.profileCardsOf(m.id) ?? []) {
          const title = typeof c.title === 'string' ? { ru: c.title, en: c.title } : { ru: c.title['ru'] ?? c.title['en'] ?? '', en: c.title['en'] ?? c.title['ru'] ?? '' };
          panels.push({ id: `${m.id}:${c.card}`, origin: 'module' as const, moduleId: m.id, card: c.card, title, size: c.size ?? { w: 300, h: 200 }, look: c.look, uses: c.uses });
        }
      }
      setModuleProfilePanels(panels);
    },
    { immediate: true },
  );
  // API 2.2: modules learn which canvas is open
  watch(
    () => [app.active, app.view] as const,
    () => {
      const s = app.view === 'editor' && app.active ? app.sessions.get(app.active) : undefined;
      runtime?.setCanvas(s ? app.active : null, s?.meta.value.name ?? '');
    },
    { immediate: true },
  );
  (globalThis as unknown as { __vp: Record<string, unknown> }).__vp.runtime = runtime;
  app.setSessionHooks({
    modules: runtime.renderProvider,
    resolver: {
      isComputed: (type) => runtime!.isComputed(type),
      compute: (obj, inputs, signal, timeout) => runtime!.compute(obj, inputs, signal, timeout),
    },
    onObjectsChanged: (ids) => runtime?.notifyChanged(ids),
    diagramCtx,
  });
  await runtime.init();
  await syncShipped();
  refreshList();
  modulesState.ready = true;
  watch(locale, (l) => {
    runtime?.setLocale(l);
    for (const s of app.sessions.values()) {
      s.scene.displayCtx = { ...s.scene.displayCtx, unavailableText: t('props.unavailable'), frameLabel: t('props.frameUntitled') };
      s.scene.invalidateObjects(null);
    }
    refreshList();
  });
  watch(() => ui.zoom, (z) => runtime?.setZoom(z));
  watch(() => ui.selection, (ids) => runtime?.notifySelection(ids));
  // API 4.6: a module document refused by its policy is a violation; a sandbox that is not isolated shows nothing
  onSandboxReport((moduleId, kind, detail) => {
    if (kind === 'csp') runtime?.sandboxViolation(moduleId, 'csp ' + detail);
    else sandboxUnsafe(moduleId);
  });
  window.addEventListener('pagehide', () => runtime?.stopAll());
}

/** Editor hooks for module object types. */
export const moduleEditorHost: Partial<EditorHost> = {
  captureKey(objectId, e, type) {
    if (ui.readOnly) return false;
    return runtime?.sendKey(objectId, { type, key: e.key, code: e.code, shift: e.shiftKey, alt: e.altKey, repeat: e.repeat }) ?? false;
  },
  typeFormatVersion(type) {
    return runtime?.typeFormatVersion(type);
  },
  typeCaps(type) {
    const info = runtime?.typeInfo(type);
    if (!info || !runtime?.isModuleAvailable(info.moduleId)) return { resize: false, rotate: false, editText: false, move: true };
    const c = info.contribution.capabilities ?? {};
    return { resize: c.resize ?? true, rotate: c.rotate ?? false, editText: !!info.contribution.text, move: true };
  },
  typeText(type) {
    return moduleTextInfo(type);
  },
  onToolGesture(tool, gesture) {
    if (!runtime || !tool.moduleId) return;
    const local = tool.id.slice(`mod:${tool.moduleId}:`.length);
    runtime
      .toolGesture(tool.moduleId, local, gesture)
      .then((ids) => {
        const found = ids.filter((id) => live.editor?.doc.get(id));
        if (found.length) live.editor?.select(found);
      })
      .catch((e) => appRef?.notify('warning', t('error.unknown', { msg: e instanceof Error ? e.message : String(e) })));
  },
  typeActions(type) {
    return !!runtime?.typeInfo(type)?.contribution.capabilities?.actions;
  },
  onAction(objectId, action) {
    runtime?.objectAction(objectId, action);
  },
  typeStyle(type) {
    const s = runtime?.typeInfo(type)?.contribution.defaultStyle;
    return s ? (structuredClone(s) as Partial<Style>) : undefined;
  },
  createDefaults(type, size) {
    if (!runtime) return Promise.resolve({});
    return runtime.createDefaults(type, size);
  },
  conflict(obj, op) {
    const r = runtime?.conflict(obj, op);
    return r ? t('modules.conflict', { op: t('modules.ops.' + op) }) : null;
  },
};

function toolsFor(): ToolDef[] {
  if (!runtime) return [];
  const out: ToolDef[] = [];
  for (const { moduleId, manifest } of runtime.contributions()) {
    for (const tool of manifest.contributes.tools ?? []) {
      if (tool.gesture) {
        out.push({ id: `mod:${moduleId}:${tool.id}`, kind: 'gesture', gesture: tool.gesture, label: tm(moduleId, tool.name), icon: tool.icon?.startsWith('<svg') ? tool.icon : undefined, moduleId });
        continue;
      }
      const type = manifest.contributes.objectTypes?.find((x) => x.type === tool.objectType);
      if (!type) continue;
      out.push({
        id: `mod:${moduleId}:${tool.id}`,
        kind: 'box',
        objectType: `${moduleId}:${type.type}`,
        defaultSize: type.defaultSize,
        label: tm(moduleId, tool.name),
        icon: tool.icon?.startsWith('<svg') ? tool.icon : undefined,
        moduleId,
      });
    }
  }
  return out;
}

/** Register module tools in an editor; re-registers when modules change (§6.2). */
export function registerModuleTools(editor: Editor): () => void {
  let unregs: (() => void)[] = [];
  const sync = () => {
    for (const u of unregs) u();
    unregs = toolsFor().map((def) => editor.registerTool(def));
  };
  sync();
  toolListeners.add(sync);
  return () => {
    toolListeners.delete(sync);
    for (const u of unregs) u();
  };
}

export function moduleTypeLabel(type: string): string | undefined {
  const info = runtime?.typeInfo(type);
  return info ? tm(info.moduleId, info.contribution.name) : undefined;
}

export function typeContribution(type: string) {
  return runtime?.typeInfo(type) ?? null;
}

export function isObjectUnavailable(o: ObjectRecord): boolean {
  return runtime ? runtime.isUnavailable(o) : !o.type.startsWith('core:');
}

export function runtimeStats(): string {
  if (!runtime) return '—';
  const s = runtime.stats();
  return `${s.running}/${s.total} · ${s.pending}`;
}

export function installedModuleIds(): string[] {
  return runtime ? runtime.list().map((m) => m.id) : [];
}

export function moduleVersions(): Record<string, string> {
  return Object.fromEntries((runtime?.list() ?? []).map((m) => [m.id, m.version]));
}

/** Module commands applicable to the current selection. */
export function commandsFor(objs: ObjectRecord[]): { moduleId: string; id: string; label: string }[] {
  if (!runtime) return [];
  const out: { moduleId: string; id: string; label: string }[] = [];
  for (const { moduleId, manifest } of runtime.contributions()) {
    for (const c of manifest.contributes.commands ?? []) {
      const types = c.objectTypes?.map((x) => `${moduleId}:${x}`);
      if (types && !objs.some((o) => types.includes(o.type))) continue;
      out.push({ moduleId, id: c.id, label: tm(moduleId, c.name) });
    }
  }
  return out;
}

/** API 2.2: module commands in the context menu for this selection (their `objectTypes` apply). */
export function contextMenuFor(objs: ObjectRecord[]): { moduleId: string; id: string; label: string }[] {
  if (!runtime) return [];
  const visible = commandsFor(objs);
  const out: { moduleId: string; id: string; label: string }[] = [];
  for (const { moduleId, manifest } of runtime.contributions()) {
    if (!runtime.isModuleAvailable(moduleId)) continue;
    for (const item of manifest.contributes.contextMenu ?? []) {
      const c = visible.find((x) => x.moduleId === moduleId && x.id === item.command);
      if (c) out.push(c);
    }
  }
  return out;
}

/** API 2.2: shortcuts of running modules (for the shortcuts reference). */
export function moduleShortcuts(): { keys: string; label: string; moduleId: string; command: string }[] {
  if (!runtime) return [];
  const out: { keys: string; label: string; moduleId: string; command: string }[] = [];
  for (const { moduleId, manifest } of runtime.contributions()) {
    if (!runtime.isModuleAvailable(moduleId)) continue;
    for (const s of manifest.contributes.shortcuts ?? []) {
      const c = manifest.contributes.commands?.find((x) => x.id === s.command);
      if (c) out.push({ keys: s.keys, label: tm(moduleId, c.name), moduleId, command: s.command });
    }
  }
  return out;
}

/**
 * API 2.2: the module command a key press means, if any — the platform's own keys are handled first,
 * the command must apply to the selection (its `objectTypes`).
 */
export function moduleShortcutFor(e: KeyboardEvent, key: string, objs: ObjectRecord[]): { moduleId: string; command: string } | null {
  if (!/^[a-z0-9]$/.test(key)) return null;
  const pressed = `${e.ctrlKey || e.metaKey ? 'Ctrl+' : ''}${e.altKey ? 'Alt+' : ''}${e.shiftKey ? 'Shift+' : ''}${key.toUpperCase()}`;
  const hit = moduleShortcuts().find((s) => s.keys === pressed);
  if (!hit) return null;
  return commandsFor(objs).some((c) => c.moduleId === hit.moduleId && c.id === hit.command) || !runtime?.get(hit.moduleId)?.manifest.contributes.commands?.find((c) => c.id === hit.command)?.objectTypes ? hit : null;
}

/** API 2.2: templates of running modules ("More" menu). */
export function moduleTemplates(): { moduleId: string; id: string; label: string; hint?: string }[] {
  void modulesState.rev;
  return (runtime?.templates() ?? []).map((x) => ({ moduleId: x.moduleId, id: x.id, label: tm(x.moduleId, x.name), hint: x.description ? tm(x.moduleId, x.description) : undefined }));
}

/** API 2.2: insert a template in the middle of the view; it gets selected. */
export function insertModuleTemplate(moduleId: string, id: string): void {
  const ed = live.editor;
  const r = live.renderer;
  if (!runtime || !ed || !r || ui.readOnly) return;
  try {
    // the template's middle lands in the middle of the view, in one step
    const ids = runtime.insertTemplate(moduleId, id, { x: r.camera.cx, y: r.camera.cy });
    const top = ids.map((i) => ed.doc.get(i)).filter((o): o is ObjectRecord => !!o && !o.parentId && !o.frameId && o.type !== 'core:connector');
    if (top.length) ed.select(top.map((o) => o.id));
  } catch (e) {
    appRef?.notify('error', t('modules.templateFailed', { msg: e instanceof Error ? e.message : String(e) }));
  }
}

/** API 2.2: import or export formats of running modules (canvas menu). */
export function moduleFormats(kind: 'import' | 'export'): { moduleId: string; id: string; label: string; accept?: string[] }[] {
  if (!runtime) return [];
  const out: { moduleId: string; id: string; label: string; accept?: string[] }[] = [];
  for (const { moduleId, manifest } of runtime.contributions()) {
    if (!runtime.isModuleAvailable(moduleId)) continue;
    for (const f of manifest.contributes.formats ?? []) if (f.kind === kind) out.push({ moduleId, id: f.id, label: tm(moduleId, f.name), accept: f.accept });
  }
  return out;
}

/** API 2.2: the user picks files, the module turns them into objects; the platform shows them. */
export async function runImport(moduleId: string, formatId: string, accept?: string[]): Promise<void> {
  if (!runtime) return;
  try {
    const files = await readPicked(await pickFiles(accept, true));
    if (!files.length) return;
    const ids = (await runtime.importFiles(moduleId, formatId, files)).filter((id) => live.editor?.doc.get(id));
    if (ids.length) {
      live.editor?.select(ids);
      live.editor?.fitObjects(ids);
    }
  } catch (e) {
    appRef?.notify('error', t('modules.importFailed', { msg: e instanceof Error ? e.message : String(e) }));
  }
}

/** API 2.2: the selection (or the whole canvas) as a file of a module's format. */
export async function runExport(moduleId: string, formatId: string): Promise<void> {
  if (!runtime) return;
  try {
    const r = await runtime.exportFile(moduleId, formatId, live.editor?.selection ?? []);
    saveBlob(r.name, r.data, r.type);
  } catch (e) {
    appRef?.notify('error', t('modules.exportFailed', { msg: e instanceof Error ? e.message : String(e) }));
  }
}

export async function runCommand(moduleId: string, id: string, selection = live.editor?.selection ?? []): Promise<void> {
  try {
    await runtime?.command(moduleId, id, selection);
  } catch (e) {
    appRef?.notify('warning', t('error.unknown', { msg: e instanceof Error ? e.message : String(e) }));
  }
}

/** Typed link types that can connect `from` → `to` (§8.4). */
export function linkTypesFor(from: ObjectRecord, to: ObjectRecord): { type: string; label: string }[] {
  if (!runtime) return [];
  const out: { type: string; label: string }[] = [];
  for (const { moduleId, manifest } of runtime.contributions()) {
    for (const lt of manifest.contributes.linkTypes ?? []) {
      if (lt.from?.length && !lt.from.includes(from.type)) continue;
      if (lt.to?.length && !lt.to.includes(to.type)) continue;
      out.push({ type: `${moduleId}:${lt.type}`, label: tm(moduleId, lt.name) });
    }
  }
  return out;
}

export function linkTypeLabel(type: string): string {
  const owner = typeOwner(type);
  if (!owner || !runtime) return type;
  const lt = runtime.get(owner)?.manifest.contributes.linkTypes?.find((l) => `${owner}:${l.type}` === type);
  return lt ? tm(owner, lt.name) : type;
}

// --- module management (§7.6) ---

export async function installModuleFile(file: File, granted?: Permission[]): Promise<ModuleManifest | null> {
  return installData(new Uint8Array(await file.arrayBuffer()), { devMode: settings.devMode, granted });
}

/** Install a package; messages for the user on success and failure (§7.6, TZ 4.3). */
async function installData(data: Uint8Array, opts: { devMode: boolean; granted?: Permission[]; official?: { keyId: string; signedAt: string } }): Promise<ModuleManifest | null> {
  if (!runtime || !appRef) return null;
  try {
    const res = await runtime.install(data, opts);
    appRef.notify('info', res.updated ? t('modules.updated', { name: tm(res.manifest.id, res.manifest.name), v: res.manifest.version }) : t('modules.installed', { name: tm(res.manifest.id, res.manifest.name), v: res.manifest.version }));
    if (res.updated && res.previous) {
      // §7.7: offer rollback when the new version cannot handle existing objects
      const objs = [...appRef.sessions.values()].flatMap((s) => [...s.doc.allObjects()]);
      const n = runtime.incompatibleObjects(res.manifest, objs);
      if (n > 0 && (await ask(t('modules.updateIncompatible', { name: tm(res.manifest.id, res.manifest.name), n })))) await runtime.rollback(res.manifest.id, res.previous);
    }
    return res.manifest;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if ((e as { code?: string }).code === 'E_DEPENDENCY') {
      appRef.notify('error', t('modules.dependency', { what: msg.replace(/^needs module /, '') }));
      return null;
    }
    if ((e as { code?: string }).code === 'E_API') {
      const range = /requires API (\S+)/.exec(msg)?.[1] ?? '?';
      // made for an earlier major of the API: the module needs a new version, not a newer platform
      const major = Number(/(\d+)/.exec(range)?.[1] ?? NaN);
      const older = major < Number(API_VERSION.split('.')[0]);
      appRef.notify('error', t(older ? 'modules.oldApi' : 'modules.incompatibleApi', { range, api: API_VERSION }));
      return null;
    }
    appRef.notify('error', t('modules.invalid', { reason: msg }));
    return null;
  }
}

// --- the catalog (TZ 5.2): checked packages install without developer mode, marked official

function catalogError(e: unknown): string {
  if (e instanceof CatalogInstallError) return e.code === 'invalid' ? t('modules.invalid', { reason: e.message }) : t(`catalog.err.${e.code}`);
  // not a known catalog problem: say what it is rather than blame the connection
  return t('modules.invalid', { reason: e instanceof Error ? e.message : String(e) });
}

/** Open the confirmation for a module of the catalog (Install button, `#install=` link). */
export async function startCatalogInstall(id: string, version?: string): Promise<void> {
  installState.error = '';
  installState.busy = true;
  try {
    const checked = await fetchForInstall(id, version);
    // every requested permission is on by default; the user can switch them off in the dialog
    installState.pending = { ...checked, granted: new Set(checked.manifest.permissions), source: 'catalog' };
  } catch (e) {
    appRef?.notify('error', catalogError(e));
  } finally {
    installState.busy = false;
  }
}

/**
 * Grants offered for an update: what the user granted before (a narrowed `own` stays `own`), and the
 * permissions the old version did not ask for at all, on — the user sees them in the dialog.
 */
function updateGrants(requested: readonly Permission[], before: readonly Permission[], granted: readonly Permission[]): Permission[] {
  const family = (p: Permission) => p.replace(/\.(all|own)$/, '');
  const asked = new Set(before.map(family));
  return [...keptGrants(requested, granted), ...requested.filter((p) => !asked.has(family(p)))];
}

/** A file picked by the user: a signed one (from the showcase) goes to the same confirmation. */
export async function startFileInstall(file: File): Promise<'signed' | 'plain'> {
  const data = new Uint8Array(await file.arrayBuffer());
  if (!openEnvelope(data)) return 'plain';
  try {
    const checked = await checkEnvelope(data);
    const existing = runtime?.get(checked.manifest.id);
    installState.pending = { ...checked, granted: new Set(existing ? updateGrants(checked.manifest.permissions, existing.manifest.permissions, existing.granted) : checked.manifest.permissions), source: 'file' };
  } catch (e) {
    appRef?.notify('error', catalogError(e));
  }
  return 'signed';
}

export async function confirmPendingInstall(): Promise<void> {
  const p = installState.pending;
  if (!p) return;
  installState.pending = null;
  // plain values: the pending install lives in reactive state, and IndexedDB cannot store proxies
  const raw = toRaw(p);
  await installData(raw.data, { devMode: false, granted: [...raw.granted], official: { keyId: raw.official.keyId, signedAt: raw.official.signedAt } });
}

export async function uninstallModule(id: string): Promise<void> {
  const info = runtime?.get(id);
  if (!runtime || !info) return;
  await runtime.uninstall(id);
}


export function countObjectsOf(moduleId: string): number {
  if (!appRef) return 0;
  let n = 0;
  for (const s of appRef.sessions.values()) for (const o of s.doc.allObjects()) if (typeOwner(o.type) === moduleId) n++;
  return n;
}

/**
 * Style controls for a module object type: `style` — fill, stroke (manifest `capabilities.style`);
 * `text` — the text chip (types with real text, manifest `text`). Unavailable modules get neither.
 */
export function moduleStyleCaps(type: string): { style: boolean; text: boolean } {
  const info = runtime?.typeInfo(type);
  if (!info || !runtime?.isModuleAvailable(info.moduleId)) return { style: false, text: false };
  return { style: !!info.contribution.capabilities?.style, text: !!info.contribution.text };
}

/** API 2.1: where a module type wants its properties by default (manifest `propertiesIn`). */
export function moduleDockDefault(type: string): boolean | undefined {
  const where = runtime?.typeInfo(type)?.contribution.propertiesIn;
  return where === undefined ? undefined : where === 'sidebar';
}

/** API 2.0: panels of running modules (manifest `contributes.panel`). */
export function modulePanels(): { moduleId: string; title: string; icon?: string; width: number; height: number }[] {
  void modulesState.rev;
  if (!runtime) return [];
  const out: { moduleId: string; title: string; icon?: string; width: number; height: number }[] = [];
  for (const m of modulesState.list) {
    const p = m.manifest.contributes.panel;
    if (!p || m.state !== 'running') continue;
    out.push({ moduleId: m.id, title: tm(m.id, p.title), icon: p.icon, width: p.width ?? 320, height: p.height ?? 420 });
  }
  return out;
}

export function modulePanelHtml(moduleId: string): string | null {
  return runtime?.panelHtml(moduleId) ?? null;
}

/**
 * API 4.6: a message from one of the module's sandboxed documents may be passed on — within the rate
 * and size limits (otherwise dropped and counted as a violation of the module).
 */
export function fromSandbox(moduleId: string, data: unknown): boolean {
  return runtime?.acceptSandboxMessage(moduleId, messageSize(data)) ?? false;
}

/** API 4.6: the sandbox flags of the module's views and windows, from its granted permissions. */
export function sandboxDoc(moduleId: string, html: string | null | undefined): SandboxDoc {
  const granted = modulesState.list.find((m) => m.id === moduleId)?.granted ?? [];
  return { html, fullscreen: granted.includes('fullscreen'), pointerLock: granted.includes('pointer-lock') };
}

/** API 4.6: the module may keep the keyboard in its live views and windows. */
export function moduleHasKeyboard(moduleId: string): boolean {
  return !!modulesState.list.find((m) => m.id === moduleId)?.granted.includes('keyboard');
}

export function sendPanelMessage(moduleId: string, data: unknown): void {
  runtime?.sendPanelMessage(moduleId, data);
}

export function sendViewMessage(moduleId: string, objectId: Id, data: unknown): void {
  runtime?.sendViewMessage(moduleId, objectId, data);
}

export async function moduleView(type: string, kind: 'object' | 'panel', objectId: Id): Promise<string> {
  if (!runtime) throw new Error('runtime not ready');
  return runtime.view(type, kind, objectId);
}
